import {
  fetchArticlesByPaths,
  getMoreChartBeatRecommendedArticleFeeds,
} from "@helper/getChartBeatRecommendation";
import { useCallback, useEffect, useState } from "react";

import { ProcessedArticleData } from "./transformer/useOSResponse";

type useChartBeatRecommendedArticlesData = {
  articles: ProcessedArticleData[];
  loading: boolean;
  error: boolean;
  isPlaceholder: boolean;
  refetch: () => Promise<void>;
};

type useChartBeatRecommendedArticlesProps = {
  enabled?: boolean;
  initialArticles?: ProcessedArticleData[];
};

/**
 * Custom React Hook: useChartBeatRecommendedArticles
 * Description: Fetches recommended articles directly from ChartBeat API
 *
 * Implements ChartBeat User Segment Personalized API best practices:
 * - Calls ChartBeat API directly from client (device/region tailored)
 * - Device type and geographic location inferred from browser/IP
 * - No user identification required - privacy-preserving
 * - Always fetch fresh recommendations - NO CACHING
 * - Detect and handle placeholder recommendations gracefully
 * - Preserve cb_rec query parameter for click tracking
 *
 * @param props - Hook configuration (size, enabled, initialArticles)
 * @returns Hook data including articles, loading state, and placeholder status
 */
export default function useChartBeatRecommendedArticles({
  enabled = true,
  initialArticles = [],
}: useChartBeatRecommendedArticlesProps): useChartBeatRecommendedArticlesData {
  const [articles, setArticles] =
    useState<ProcessedArticleData[]>(initialArticles);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [isPlaceholder, setIsPlaceholder] = useState(false);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError(false);
    setIsPlaceholder(false);

    try {
      // Step 1: Fetch ChartBeat recommendations directly from front-end
      // Device type and geographic location are automatically inferred
      const { articlePaths, isPlaceholder: isPlaceholderResponse } =
        await getMoreChartBeatRecommendedArticleFeeds();

      setIsPlaceholder(isPlaceholderResponse);
      console.log("ChartBeat returned article paths:", articlePaths);

      if (isPlaceholderResponse) {
        console.log(
          "ChartBeat returned placeholder recommendations. Returning top articles until tailored results are ready.",
        );
      }

      if (articlePaths.length === 0) {
        return;
      }

      // Step 2: Fetch article details from our API
      // The article paths from ChartBeat already include the cb_rec parameter for tracking
      const articlesData = await fetchArticlesByPaths(articlePaths);
      console.log(
        "Fetched article details for ChartBeat recommendations:",
        articlesData,
      );

      setArticles(articlesData);
    } catch (err) {
      console.error("Failed to fetch recommended articles", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial + dependency change fetch
  useEffect(() => {
    if (!enabled) return;

    void fetchArticles();
  }, [enabled, fetchArticles]);

  return {
    articles,
    loading,
    error,
    isPlaceholder,
    refetch: fetchArticles,
  };
}
